import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Search,
  Bell,
  Settings,
  ChevronDown,
  Home,
  Users,
  UserCheck,
  Building2,
  TrendingUp,
  BarChart3,
  Menu,
  X,
  HeadphonesIcon,
  UserCog,
  RefreshCw,
  Briefcase,
  PieChart
} from "lucide-react";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigationItems = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Leads", path: "/leads", icon: UserCheck },
    { name: "Contacts", path: "/contacts", icon: Users },
    { name: "Accounts", path: "/accounts", icon: Building2 },
    { name: "User Management", path: "/user_manageMent", icon: UserCog },
    { name: "Deals", path: "/deals", icon: Briefcase },
    { name: "Reports", path: "/reports", icon: PieChart },
    { name: "Support", path: "/support", icon: HeadphonesIcon },
    { name: "Synchronizing", path: "/integration", icon: RefreshCw },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`bg-white shadow-sm border-r border-gray-200 transition-all duration-300 ${
          isSidebarOpen ? "w-64" : "w-16"
        } md:w-64`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <img
              className="h-10"
              src={"https://bsquaress.com/assest/logo.png"}
              alt="BSQUARE Logo"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="md:hidden"
          >
            {isSidebarOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
          </Button>
        </div>

        <nav className="mt-4 px-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);

            return (
              <button
                key={item.name}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors duration-200 mb-1 ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${
                    isActive ? "text-blue-700" : "text-gray-500"
                  }`}
                />
                <span
                  className={`font-medium ${
                    !isSidebarOpen && "hidden"
                  } md:block`}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="md:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="search"
                  placeholder="Search leads, contacts, accounts..."
                  className="pl-10 w-80 bg-gray-50 border-gray-200"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>

              <Button variant="ghost" size="icon">
                <Settings className="h-5 w-5" />
              </Button>

              <div className="flex items-center space-x-2 border-l pl-4">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-600 text-white">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500">{user?.role}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gray-50">{children}</main>
      </div>
    </div>
  );
};

export default Layout;